import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import {
    Gauge, AlertTriangle, TrendingDown, Sparkles, Download, ArrowRight,
    ShieldCheck, ChevronRight, Briefcase, User, Users, Radar,
} from "lucide-react";

/**
 * The "WOW bundle" strip that lives above the Financial Position section
 * on the Command Centre. Five cards, one round-trip:
 *   • Finance Health Score (from /api/health-score)
 *   • Money Leak Detector  (from /api/insights/money-leak)
 *   • Personal vs Business (from /api/insights/personal-business)
 *   • Subscription Discovery (from /api/subscriptions/detect/candidates/count)
 *   • CA Ready Pack download (calls /api/ca-pack/download)
 */

const BAND_STYLE = (band) => {
    if (band === "Excellent" || band === "Strong") return { ring: "stroke-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700" };
    if (band === "Fair" || band === "Good") return { ring: "stroke-blue-500", bg: "bg-blue-50", text: "text-blue-700" };
    if (band === "Needs Work" || band === "Needs Attention") return { ring: "stroke-amber-500", bg: "bg-amber-50", text: "text-amber-700" };
    return { ring: "stroke-rose-500", bg: "bg-rose-50", text: "text-rose-700" };
};

function HealthScoreCard() {
    const [d, setD] = useState(null);
    useEffect(() => {
        api.get("/health-score").then((r) => setD(r.data)).catch(() => setD(null));
    }, []);
    if (!d) return <div className="h-56 bg-stone-50 rounded-2xl animate-pulse" />;
    const tone = BAND_STYLE(d.band);
    const R = 32;
    const C = 2 * Math.PI * R;
    const offset = C - (Math.max(0, Math.min(100, d.percent)) / 100) * C;
    const worst = [...(d.factors || [])].sort((a, b) => (a.points / a.max) - (b.points / b.max))[0];

    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 flex flex-col dark:bg-stone-900 dark:border-stone-800" data-testid="wow-health-score">
            <div className="flex items-center gap-2 mb-2">
                <Sparkles size={13} className="text-stone-500" />
                <div className="eyebrow">Finance Health Score</div>
            </div>
            <div className="flex items-center gap-4 mb-3">
                <div className="relative w-20 h-20">
                    <svg viewBox="0 0 80 80" className="w-full h-full -rotate-90">
                        <circle cx="40" cy="40" r={R} strokeWidth="6" className="stroke-stone-100 fill-none dark:stroke-stone-700" />
                        <circle
                            cx="40" cy="40" r={R} strokeWidth="6"
                            strokeDasharray={C}
                            strokeDashoffset={offset}
                            className={`${tone.ring} fill-none transition-all duration-700`}
                            strokeLinecap="round"
                        />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xl font-bold tabular-nums text-stone-900 dark:text-white" data-testid="wow-health-score-value">
                            {Math.round(d.percent)}
                        </span>
                    </div>
                </div>
                <div>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`}>
                        <Gauge size={10} /> {d.band}
                    </span>
                    <div className="text-[12px] text-stone-500 mt-1">
                        {Math.round(d.score)} / {d.max} points
                    </div>
                </div>
            </div>
            {worst && worst.recommendation && (
                <div className="mt-auto pt-3 border-t border-stone-100 dark:border-stone-800">
                    <div className="eyebrow mb-1">One thing to fix</div>
                    <div className="text-[12px] text-stone-700 dark:text-stone-300 leading-snug">{worst.recommendation}</div>
                    {worst.action_path && (
                        <Link
                            to={worst.action_path}
                            className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-stone-900 dark:text-white hover:underline"
                            data-testid="wow-health-fix"
                        >
                            {worst.action_label || "Fix now"} <ChevronRight size={11} />
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}

function MoneyLeakCard() {
    const [d, setD] = useState(null);
    useEffect(() => {
        api.get("/insights/money-leak").then((r) => setD(r.data)).catch(() => setD(null));
    }, []);
    if (!d) return <div className="h-56 bg-stone-50 rounded-2xl animate-pulse" />;
    const isClean = d.total_inr === 0 && (d.items || []).length === 0;

    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 flex flex-col dark:bg-stone-900 dark:border-stone-800" data-testid="wow-money-leak">
            <div className="flex items-center gap-2 mb-2">
                <TrendingDown size={13} className="text-stone-500" />
                <div className="eyebrow">Money Leak Detector</div>
            </div>
            {isClean ? (
                <div className="flex-1 flex items-center gap-3 text-emerald-800 bg-emerald-50 rounded-lg px-3 py-4 dark:bg-emerald-900/20 dark:text-emerald-200">
                    <ShieldCheck size={20} />
                    <div>
                        <div className="font-medium">No leaks this month.</div>
                        <div className="text-[12px] opacity-80">Clean run — nothing avoidable found.</div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="text-[28px] font-bold tabular-nums text-rose-700 leading-none">
                        {fmtMoney(d.total_inr)}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-1">
                        potentially leaking this month across {d.signal_count} signal{d.signal_count === 1 ? "" : "s"}
                    </div>
                    <ul className="mt-3 space-y-1 flex-1">
                        {(d.items || []).slice(0, 3).map((it) => (
                            <li key={it.key} className="flex items-center justify-between text-[12px]" data-testid={`wow-leak-${it.key}`}>
                                <span className="truncate flex items-center gap-1.5">
                                    <AlertTriangle size={11} className={it.tone === "rose" ? "text-rose-500" : "text-amber-500"} />
                                    <span className="truncate">{it.label}</span>
                                </span>
                                <span className="tabular-nums font-medium ml-2 shrink-0">
                                    {it.amount > 0 ? fmtMoney(it.amount) : `${it.count}×`}
                                </span>
                            </li>
                        ))}
                    </ul>
                    {(d.items || []).length > 0 && d.items[0].path && (
                        <Link
                            to={d.items[0].path}
                            className="mt-3 pt-3 border-t border-stone-100 inline-flex items-center gap-1 text-[12px] font-medium text-stone-900 dark:text-white hover:underline dark:border-stone-800"
                            data-testid="wow-leak-fix"
                        >
                            {d.items[0].cta} <ChevronRight size={11} />
                        </Link>
                    )}
                </>
            )}
        </div>
    );
}

const BUCKET_META = {
    Personal: { icon: User, color: "text-blue-700 bg-blue-50", bar: "bg-blue-500" },
    Business: { icon: Briefcase, color: "text-purple-700 bg-purple-50", bar: "bg-purple-500" },
    Family: { icon: Users, color: "text-teal-700 bg-teal-50", bar: "bg-teal-500" },
};

function BusinessSplitCard() {
    const [d, setD] = useState(null);
    useEffect(() => {
        api.get("/insights/personal-business").then((r) => setD(r.data)).catch(() => setD(null));
    }, []);
    if (!d) return <div className="h-56 bg-stone-50 rounded-2xl animate-pulse" />;
    const buckets = Object.entries(d.buckets || {});

    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 flex flex-col dark:bg-stone-900 dark:border-stone-800" data-testid="wow-personal-business">
            <div className="flex items-center gap-2 mb-2">
                <Briefcase size={13} className="text-stone-500" />
                <div className="eyebrow">Personal / Business</div>
            </div>
            <div className="text-[22px] font-bold tabular-nums text-stone-900 dark:text-white leading-none">
                {fmtMoney(d.grand_total || 0)}
            </div>
            <div className="text-[11px] text-stone-500 mt-1">total spend this month</div>

            {buckets.length === 0 ? (
                <div className="mt-4 text-[12px] text-stone-500 bg-stone-50 rounded-lg p-3 flex-1 dark:bg-stone-800">
                    No expenses recorded this month. Log one to see how it splits.
                </div>
            ) : (
                <>
                    {/* Stacked bar */}
                    <div className="mt-3 flex h-2 rounded-full overflow-hidden bg-stone-100 dark:bg-stone-800" data-testid="wow-split-bar">
                        {buckets.map(([name, v]) => {
                            const meta = BUCKET_META[name] || { bar: "bg-stone-500" };
                            return <div key={name} className={meta.bar} style={{ width: `${v.percent}%` }} />;
                        })}
                    </div>
                    <ul className="mt-3 space-y-1.5 flex-1">
                        {buckets.map(([name, v]) => {
                            const meta = BUCKET_META[name] || { icon: User, color: "text-stone-700 bg-stone-100" };
                            const Ic = meta.icon;
                            return (
                                <li key={name} className="flex items-center gap-2 text-[12px]" data-testid={`wow-split-${name}`}>
                                    <span className={`w-5 h-5 rounded flex items-center justify-center ${meta.color}`}>
                                        <Ic size={11} />
                                    </span>
                                    <span className="flex-1">{name}</span>
                                    <span className="tabular-nums font-medium">{fmtMoney(v.total)}</span>
                                    <span className="tabular-nums text-stone-500 w-10 text-right">{v.percent}%</span>
                                </li>
                            );
                        })}
                    </ul>
                    {(d.buckets?.Business?.gst_eligible > 0 || d.buckets?.Business?.reimbursable > 0) && (
                        <div className="mt-3 pt-3 border-t border-stone-100 text-[11px] text-stone-500 space-y-0.5 dark:border-stone-800">
                            {d.buckets?.Business?.reimbursable > 0 && (
                                <div>Reimbursable: <span className="tabular-nums text-stone-800 dark:text-stone-200">{fmtMoney(d.buckets.Business.reimbursable)}</span></div>
                            )}
                            {d.buckets?.Business?.gst_eligible > 0 && (
                                <div>GST-eligible: <span className="tabular-nums text-stone-800 dark:text-stone-200">{fmtMoney(d.buckets.Business.gst_eligible)}</span></div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function SubDiscoveryCard() {
    const [count, setCount] = useState(null);
    const [scanning, setScanning] = useState(false);

    const load = useCallback(() => {
        api.get("/subscriptions/detect/candidates/count")
            .then((r) => setCount(r.data?.count ?? 0))
            .catch(() => setCount(0));
    }, []);
    useEffect(() => { load(); }, [load]);

    const scan = async () => {
        setScanning(true);
        try {
            const { data } = await api.post("/subscriptions/detect/scan", { months: 6 });
            toast.success(`Scanned ${data.expenses_analysed} txns · ${data.candidates_total} candidates ready`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Scan failed");
        } finally {
            setScanning(false);
        }
    };

    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 flex flex-col dark:bg-stone-900 dark:border-stone-800" data-testid="wow-sub-discovery">
            <div className="flex items-center gap-2 mb-2">
                <Radar size={13} className="text-stone-500" />
                <div className="eyebrow">Subscription Discovery</div>
            </div>
            <div className="text-[28px] font-bold tabular-nums text-stone-900 dark:text-white leading-none" data-testid="wow-sub-count">
                {count === null ? "…" : count}
            </div>
            <div className="text-[11px] text-stone-500 mt-1">candidate subscription{count === 1 ? "" : "s"} pending review</div>

            {count === 0 && (
                <div className="mt-3 text-[12px] text-stone-600 bg-stone-50 rounded-lg p-3 flex-1 dark:bg-stone-800 dark:text-stone-300">
                    Run a scan across the last 6 months of expenses. We&apos;ll surface Netflix-style recurring
                    payments plus any merchant that hits 3+ times.
                </div>
            )}
            {count > 0 && (
                <p className="mt-3 text-[12px] text-stone-600 dark:text-stone-300 flex-1">
                    We spotted recurring payments in your expenses. Review each and add the ones you want tracked as subscriptions.
                </p>
            )}

            <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-3 dark:border-stone-800">
                <button
                    onClick={scan}
                    disabled={scanning}
                    className="text-[12px] font-medium text-stone-700 hover:text-stone-900 disabled:opacity-50 dark:text-stone-300"
                    data-testid="wow-sub-scan"
                >
                    {scanning ? "Scanning…" : "Scan again"}
                </button>
                {count > 0 && (
                    <Link
                        to="/subscriptions?tab=candidates"
                        className="ml-auto inline-flex items-center gap-1 text-[12px] font-medium text-stone-900 dark:text-white hover:underline"
                        data-testid="wow-sub-review"
                    >
                        Review {count} <ChevronRight size={11} />
                    </Link>
                )}
            </div>
        </div>
    );
}

function CAPackCard() {
    const [busy, setBusy] = useState(false);
    const download = async () => {
        setBusy(true);
        try {
            const month = new Date().toISOString().slice(0, 7);
            const { data } = await api.get(`/ca-pack/download?month=${month}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([data], { type: "application/zip" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `vault-ca-pack-${month}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("CA Pack downloaded · 5 Excel files + summary");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Export failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-gradient-to-br from-stone-900 to-stone-800 border border-stone-900 rounded-2xl p-5 flex flex-col text-white" data-testid="wow-ca-pack">
            <div className="flex items-center gap-2 mb-2">
                <Download size={13} className="text-stone-300" />
                <div className="eyebrow text-stone-400">CA-Ready Pack</div>
            </div>
            <div className="text-lg font-semibold mt-1">One-click hand-off</div>
            <p className="text-[12px] text-stone-300 mt-2 flex-1 leading-relaxed">
                Excel bundle with expenses, statements, payments, subscriptions, and business/personal split for the
                current month. Perfect for your CA or auditor.
            </p>
            <button
                onClick={download}
                disabled={busy}
                className="mt-3 inline-flex items-center justify-center gap-2 py-2 rounded-full bg-white text-stone-900 text-sm font-semibold hover:bg-stone-100 disabled:opacity-60 transition-colors"
                data-testid="wow-ca-pack-download"
            >
                <Download size={14} />
                {busy ? "Preparing…" : "Download pack (.zip)"}
            </button>
            <Link
                to="/auditor-shares"
                className="mt-2 text-[11px] text-stone-300 hover:text-white inline-flex items-center gap-1 justify-center"
                data-testid="wow-ca-shares"
            >
                or share live access → <ArrowRight size={10} />
            </Link>
        </div>
    );
}

export default function WowInsightsStrip() {
    return (
        <section className="mb-8" data-testid="wow-strip">
            <div className="mb-4 flex items-end justify-between">
                <div>
                    <div className="eyebrow">Insights that move the needle</div>
                    <h2 className="font-display text-2xl text-stone-900 mt-1 dark:text-white">
                        What needs your attention.
                    </h2>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                <HealthScoreCard />
                <MoneyLeakCard />
                <BusinessSplitCard />
                <SubDiscoveryCard />
                <CAPackCard />
            </div>
        </section>
    );
}
